import { useController, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  OPTIMIZE_MAX_ALLOWED,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_PERIOD,
} from 'modules/frequencyManagement/contexts/FormProvider';
import { FrequencyMode } from 'modules/frequencyManagement/lib/types';
import { periodOptions } from 'modules/frequencyManagement/components/optimize/Optimize.constants';
import { BasicSelectField } from 'lib/iterable-hook-form';
import { Body2NoMargin } from 'modules/frequencyManagement/components/Content.styles';
import { spacing } from 'ui/theme';
import { InputText, InputTextProps } from 'ui/inputs';
import { OptimizeBaseProps } from 'modules/frequencyManagement/components/optimize/Optimize.types';
import { useFocusOnMount } from 'modules/frequencyManagement/contexts/FocusContext';
import { getOptimizeInputId } from 'modules/frequencyManagement/commonUtils';

const transformToNumber = (v: string) => (v === '' ? null : Number(v));
type TriggerInputFieldProps = Omit<InputTextProps, 'value'> & {
  name: string;
  defaultValue?: string;
  localOnChange: () => void;
};
const TriggerInputField = ({
  name,
  defaultValue,
  hasError = false,
  localOnChange,
  ...rest
}: TriggerInputFieldProps) => {
  const {
    field: { onChange, onBlur, value },
    fieldState: { error },
  } = useController({
    name,
    defaultValue,
  });
  const handleChange = (v: string) => {
    onChange(transformToNumber(v));
    localOnChange();
  };

  return (
    <InputText
      {...rest}
      name={name}
      value={value}
      onBlur={onBlur}
      onChange={handleChange}
      hasError={!!error || hasError}
      type="number"
    />
  );
};

export const EditingSetWidthInput = styled(TriggerInputField)`
  & > input {
    width: ${spacing.x3};
  }
`;

export const OptimizeInputFields = ({ rootName }: OptimizeBaseProps) => {
  const { trigger } = useFormContext();
  const minInputName = rootName ? `${rootName}.${OPTIMIZE_MIN_ALLOWED}` : OPTIMIZE_MIN_ALLOWED;
  const maxInputName = rootName ? `${rootName}.${OPTIMIZE_MAX_ALLOWED}` : OPTIMIZE_MAX_ALLOWED;
  const periodSelectName = rootName ? `${rootName}.${OPTIMIZE_PERIOD}` : OPTIMIZE_PERIOD;
  const retriggerValidations = () => {
    trigger([minInputName, maxInputName]);
  };

  useFocusOnMount(FrequencyMode.Optimize, rootName);

  return (
    <>
      <EditingSetWidthInput
        id={getOptimizeInputId(rootName)}
        localOnChange={retriggerValidations}
        name={minInputName}
        Suffix={'min'}
        width={spacing.x8}
      />
      <svg width="8" height="2" viewBox="0 0 8 2" fill="none">
        <path d="M0 1H8" stroke="#999094" strokeLinecap="round" />
      </svg>
      <EditingSetWidthInput
        localOnChange={retriggerValidations}
        name={maxInputName}
        Suffix={'max'}
        width={spacing.x8}
      />
      <Body2NoMargin>messages per</Body2NoMargin>
      <BasicSelectField name={periodSelectName} options={periodOptions} />
    </>
  );
};
